import type { TutorialDefinition, ActionDefinition } from './types';
import type { Assignment, Document } from '@markeroo/shared';
import { notifications, ui } from '@/strings';

/**
 * Tutorial timing constants (in milliseconds)
 */
export const TUTORIAL_TIMEOUTS = {
  /** Standard delay between tutorial steps for better UX */
  STEP_DELAY: 1000,
  /** Short delay before validations */
  SHORT_DELAY: 500,
  /** Wait for modal to open/close */
  MODAL_TRANSITION: 300,
  /** Brief delay for React state propagation */
  STATE_PROPAGATION: 100,
  /** Wait for simulation effect */
  SIMULATION_DELAY: 2000,
  /** Button enable timeout (for form validation) */
  BUTTON_ENABLE: 5000,
  /** AI rule generation timeout */
  RULE_GENERATION: 30000,
  /** Paper save operation timeout */
  PAPER_SAVE: 5000,
  /** Processing simulation timeout */
  PROCESSING_SIMULATION: 10000,
  /** Validation polling interval */
  POLL_INTERVAL: 500,
  /** Number of polling attempts for 30s timeout (60 * 500ms = 30s) */
  POLL_ATTEMPTS_30S: 60,
} as const;

/**
 * Genghis Khan essay for tutorial demonstration (intentionally contains errors)
 */
export const TUTORIAL_ESSAY_TEXT = `Genghis Khan was one of the most powerfull leaders in history and he conquered more land then anyone else ever did. He was born around 1162 in mongolia with the name Temujin but later changed it to Genghis Khan which means "universal ruler". His early life was really hard because his father got poisoned when he was young and his family had to survive on there own in the harsh mongolian steppes, they had to eat roots and small animals just to stay alive. Even though he had a tough childhood it made him into a strong leader who would eventually rule the largest empire the world has ever seen.
The way Genghis Khan united the Mongol tribes was threw both military genuis and political strategy. Before him the mongols were just a bunch of seperate tribes that fought eachother all the time and couldn’t accomplish nothing important. He started by making alliances with other tribes and slowly building his power, he was really good at knowing who to trust and who to fight against. One of his most important strategys was promoting people based on merit not on what family they came from which was very diferent from how things were usually done back then and this made his followers very loyal to him because they new they could rise up if they worked hard. By 1206 he had united all the mongol tribes under his leadership and took the title Genghis Khan.
The mongol army under Genghis Khan was incredably effective and they conquered territories from China all the way to Eastern Europe. They’re cavalry archers could shoot arrows while riding at full speed and each soldier had multiple horses so they could travel long distances without getting tired, the mongols could cover up to 100 miles in a single day which was way faster then any other army at that time. Genghis Khan also used psychological warfare like he would sometimes destroy entire citys to scare other places into surrendering without a fight. The Mongols conquered China, Central Asia, Persia, and parts of Russia and Eastern Europe, at its peak the Mongol Empire stretched from the Pacific Ocean to the Mediterranean Sea and it was the largest contiguos land empire in history covering about 9 million square miles.
Genghis Khans impact on the world went way beyond just military conquest and effected trade, culture, and technology for centurys after his death. The Mongol Empire created the "Pax Mongolica" or Mongol Peace which made the Silk Road safe for merchants and this increased trade between East and West like never before, new ideas and inventions spread quickly across the empire. The Mongols also had religious tolerance and let people practice whatever religion they wanted which was pretty unusual for conquerers at that time. Some historians say that Genghis Khan was responsible for killing millions of people during his conquests but others argue that he modernized many aspects of society and connected different parts of the world together, its complicated to judge historical figures by todays standards.
In conclusion Genghis Khan was one of historys most influential leaders who changed the world forever even though he started from very humble beginings. He united the Mongol tribes created the largest land empire ever and revolutionized warfare trade and cultural exchange between different civilizations. While his methods were often brutal and caused alot of death and destruction his empire also brought about positive changes like increased trade religious tolerance and the exchange of ideas and technology. Today Genghis Khan is remembered as both a fearsome conquerer and a brilliant leader who shaped the course of world history. His legacy shows us that one person can have an enormous impact on the world weather for better or worse and that history is often more complicated than it first appears.`;

export const TUTORIAL_STUDENT_NAME = 'Alex Okonkwo';

export const TUTORIAL_EXAMPLE_RULE =
  "I’m working with students on making sure their thesis has a good supporting sentence";

export interface TutorialHelpers {
  onFillExampleRule: () => void;
  onFillExamplePaper: () => void;
  /** Tutorial content from backend (single source of truth) */
  tutorialContent: {
    essayText: string;
    criteriaPrompt: string;
  };
}

/**
 * Creates the full 19-step assignment tutorial
 */
export function createAssignmentTutorial(helpers: TutorialHelpers): TutorialDefinition {
  return {
    id: 'assignment-first-steps',
    name: 'Assignment First Steps Tutorial',
    // Cleanup any previous tutorial artifacts on start
    onStart: () => {
      try {
        // Remove any previously injected temporary tutorial cards
        document.querySelectorAll('[data-tutorial-target="tutorial-temp-card"]').forEach((el) => el.remove());
        // Clear generator textarea if it has tutorial value
        const ta = document.querySelector<HTMLTextAreaElement>('[data-tutorial-target="criteria-generator-textarea"]');
        if (ta) {
          const desc = Object.getOwnPropertyDescriptor(
            window.HTMLTextAreaElement.prototype,
            'value'
          );
          desc?.set?.call(ta, '');
          ta.dispatchEvent(new InputEvent('input', { bubbles: true }));
        }
      } catch {
        // Ignore cleanup errors
      }
    },
    // Also cleanup at completion
    onComplete: () => {
      try {
        document.querySelectorAll('[data-tutorial-target="tutorial-temp-card"]').forEach((el) => el.remove());
      } catch {
        // Ignore cleanup errors
      }
    },

    shouldShow: (context) => {
      const assignment = context.assignment as Assignment | undefined;
      const documents = (context.documents as Document[]) || [];

      if (!assignment) return false;

      // Only show for brand-new assignments (no documents)
      const hasDocuments = documents.length > 0;

      return !hasDocuments;
    },

    steps: [
      // ============================================================================
      // PHASE 1: INTRODUCTION
      // ============================================================================

      {
        id: 'step-1-welcome',
        title: 'Welcome to Markeroo!',
        description:
          `Markeroo provides writing feedback to students to free-up your time.

In 3 minutes, you’ll create criteria for your paper and see them in action. Let’s go!`,
        targetSelector: 'none',
        placement: 'bottom',
        allowInteraction: false,
      },

      {
        id: 'step-2-marking-rules',
        title: 'Check Your Criteria',
        description:
          `Criteria tell Markeroo what to look for when highlighting papers. You start with defaults based on your class’ ELA grade level but you can customize them to match your own teaching style.

Let’s add some custom criteria for your students.`,
        targetSelector: '[data-tutorial-target="criteria-summary"]',
        placement: 'bottom',
        allowInteraction: false,
        nextButtonLabel: 'Open Criteria',
        onNextActions: [
          {
            id: 'wait-before-click',
            type: 'wait',
            value: TUTORIAL_TIMEOUTS.STEP_DELAY,
          } as ActionDefinition,
          {
            id: 'click-manage-rules',
            type: 'click',
            targetSelector: '[data-tutorial-target="criteria-summary"] button',
            showRipple: true,
          } as ActionDefinition,
        ],
        waitFor: () => {
          // Wait for CriteriaManagerModal to open
          const modal = document.querySelector('[role="dialog"]');
          const criteriaList = document.querySelector('[data-tutorial-target="criteria-list"]');
          return modal !== null && criteriaList !== null;
        },
      },

      // ============================================================================
      // PHASE 2: CREATING A MARKING RULE
      // ============================================================================

      {
        id: 'step-3-criteria-list',
        title: 'Your Default Criteria',
        description:
          `These are your default criteria based on your class’ ELA grade level. You can use these as-is, or customize them to match your teaching style.

Let’s add some new criteria for your students.`,
        targetSelector: '[data-tutorial-target="criteria-list"]',
        placement: 'bottom',
        allowInteraction: false,
        nextButtonLabel: 'Add Criteria',
        onNextActions: [
          {
            id: 'wait-before-add-criteria',
            type: 'wait',
            value: TUTORIAL_TIMEOUTS.STEP_DELAY,
          } as ActionDefinition,
          {
            id: 'click-add-criteria',
            type: 'click',
            targetSelector: '[data-tutorial-target="criteria-list"] button',
            showRipple: true,
          } as ActionDefinition,
        ],
        waitFor: () => {
          // Wait for AddCriteriaModal to open with method selection visible
          const addCriteriaModal = document.querySelector('[role="dialog"]');
          const inputMethods = document.querySelector('[data-tutorial-target="add-criteria-input-methods"]');
          return addCriteriaModal !== null && inputMethods !== null;
        },
      },

      {
        id: 'step-4-input-methods',
        title: 'Choose Your Input Method ✨',
        description: `You have three ways to create criteria:

🎙️ **Talk** - Speak naturally as you would to another teacher about what you want your students to know
✍️ **Write** - Type what you’re teaching
✏️ **Manual** - Directly create a focus area with full control

We’ll use **Write** for this tutorial.`,
        targetSelector: '[data-tutorial-target="add-criteria-input-methods"]',
        placement: 'bottom',
        allowInteraction: false,
        nextButtonLabel: 'Use Write',
        onNextActions: [
          {
            id: 'wait-before-click-text-method',
            type: 'wait',
            value: TUTORIAL_TIMEOUTS.STEP_DELAY,
          } as ActionDefinition,
          {
            id: 'click-text-method-option',
            type: 'click',
            targetSelector: '[data-tutorial-target="text-method-option"]',
            showRipple: true,
          } as ActionDefinition,
        ],
        waitFor: () => {
          // Wait for the text input step to appear (textarea visible)
          const textarea = document.querySelector('[data-tutorial-target="criteria-generator-textarea"]');
          return textarea !== null;
        },
      },

      {
        id: 'step-5-rule-generator',
        title: "Describe What You’re Teaching",
        description:
          `Tell Markeroo what you’re working on with your students in plain English, and we’ll magically create criteria for you ✨

Let’s fill in an example so we can see how it works.`,
        targetSelector: '[data-tutorial-target="criteria-generator-textarea"]',
        placement: 'bottom',
        allowInteraction: false,
        nextButtonLabel: 'Generate Criteria',
        timeout: TUTORIAL_TIMEOUTS.RULE_GENERATION,
        onNextActions: [
          {
            id: 'switch-highlight-to-modal',
            type: 'custom',
            onExecute: () => {
              // Switch highlight from textarea to modal body during execution
              const modal = document.querySelector('[role="dialog"]');
              if (modal) {
                modal.setAttribute('data-tutorial-target-temp', 'rule-generation-in-progress');
              }
            },
          } as ActionDefinition,
          {
            id: 'wait-before-fill',
            type: 'wait',
            value: TUTORIAL_TIMEOUTS.STEP_DELAY,
          } as ActionDefinition,
          {
            id: 'fill-rule-text',
            type: 'fill-input',
            targetSelector: '[data-tutorial-target="criteria-generator-textarea"]',
            value: helpers.tutorialContent.criteriaPrompt,
            showRipple: true,
          } as ActionDefinition,
          {
            id: 'wait-for-react-update',
            type: 'wait',
            value: 200,
          } as ActionDefinition,
          {
            id: 'wait-before-click',
            type: 'wait',
            value: TUTORIAL_TIMEOUTS.STEP_DELAY,
          } as ActionDefinition,
          {
            id: 'click-add-rules',
            type: 'click',
            targetSelector: '[data-tutorial-target="add-rules-button"]',
            showRipple: true,
          } as ActionDefinition,
        ],
        waitFor: () => {
          // Check for the presence of a newly created criteria card
          // This is more robust than counting grid children because:
          // 1. It directly checks for the expected outcome (new criteria added)
          // 2. No baseline counting needed
          // 3. Works regardless of modal state or DOM structure
          const newCriteriaCard = document.querySelector('[data-tutorial-target="new-criteria-card"]');
          return newCriteriaCard !== null;
        },
      },

      {
        id: 'step-6-rule-created',
        title: 'Your new criteria was created! 🎉',
        description:
          `That was fast! ❤️ **Click** on any focus area to customize what Markeroo looks for and how it shows up in student feedback.

Ready to see these criteria in action?`,
        targetSelector: '[data-tutorial-target="new-criteria-card"]',
        placement: 'bottom',
        allowInteraction: false,
        nextButtonLabel: 'Close Criteria',
        onNextActions: [
          {
            id: 'wait-before-close',
            type: 'wait',
            value: TUTORIAL_TIMEOUTS.STEP_DELAY,
          } as ActionDefinition,
          {
            id: 'click-close',
            type: 'click',
            targetSelector: '[data-tutorial-target="criteria-manager-close-button"]',
            showRipple: true,
          } as ActionDefinition,
        ],
        waitFor: async () => {
          await new Promise((resolve) => setTimeout(resolve, TUTORIAL_TIMEOUTS.MODAL_TRANSITION));
          const modal = document.querySelector('[data-tutorial-target="criteria-manager-close-button"]');
          return modal === null;
        },
      },

      // ============================================================================
      // PHASE 3: UPLOADING A STUDENT PAPER
      // ============================================================================

      {
        id: 'step-7-add-paper',
        title: 'Upload a Student Paper',
        description: `Let’s upload a paper and watch Markeroo highlight it using your criteria.`,
        targetSelector: '[data-tutorial-target="add-paper-button"]',
        placement: 'left',
        allowInteraction: false,
        nextButtonLabel: 'Upload Paper',
        onNextActions: [
          {
            id: 'wait-before-add-paper',
            type: 'wait',
            value: TUTORIAL_TIMEOUTS.STEP_DELAY,
          } as ActionDefinition,
          {
            id: 'click-add-paper',
            type: 'click',
            targetSelector: '[data-tutorial-target="add-paper-button"]',
            showRipple: true,
          } as ActionDefinition,
        ],
        waitFor: () => {
          const modal = document.querySelector('[data-tutorial-target="add-paper-modal"]');
          return modal !== null;
        },
      },

      {
        id: 'step-8-select-student',
        title: 'Choose the Student',
        description:
          `Each paper belongs to a student so you can track progress over time.

We’ll create an example student for you.`,
        targetSelector: '[data-tutorial-target="student-select"]',
        placement: 'bottom',
        allowInteraction: false,
        nextButtonLabel: 'Create Student',
        timeout: TUTORIAL_TIMEOUTS.PAPER_SAVE,
        onNextActions: [
          {
            id: 'select-or-create-student',
            type: 'custom',
            onExecute: async () => {
              try {
                await new Promise((resolve) =>
                  setTimeout(resolve, TUTORIAL_TIMEOUTS.STEP_DELAY)
                );

                const input = document.querySelector<HTMLInputElement>(
                  '[data-tutorial-target="student-select"]'
                );
                if (!input) {
                  throw new Error('Student select input not found');
                }

                // Focus and click to open dropdown
                input.focus();
                input.click();

                // Wait for dropdown to appear
                await new Promise((resolve) =>
                  setTimeout(resolve, TUTORIAL_TIMEOUTS.MODAL_TRANSITION)
                );

                const dropdown = document.querySelector('[role="listbox"]');
                if (!dropdown) {
                  throw new Error('Student dropdown not found');
                }

                const options = Array.from(dropdown.querySelectorAll('[role="option"]'));
                
                // Check if "Tutorial Student" already exists
                const tutorialStudentOption = options.find(
                  (opt) => opt.textContent?.includes(TUTORIAL_STUDENT_NAME)
                );

                if (tutorialStudentOption) {
                  // Student exists, just click it
                  (tutorialStudentOption as HTMLElement).click();
                } else {
                  // Student doesn’t exist, click create new
                  const createOption = options.find(
                    (opt) => opt.textContent?.includes(ui.labels.createNewStudentOption) || opt.textContent?.includes("Create new student")
                  );

                  if (!createOption) {
                    throw new Error('Create student option not found');
                  }

                  (createOption as HTMLElement).click();

                  // Wait for StudentFormModal to open
                  await new Promise((resolve) =>
                    setTimeout(resolve, TUTORIAL_TIMEOUTS.MODAL_TRANSITION)
                  );

                  // Fill name input using data-tutorial-target
                  const nameInput = document.querySelector(
                    '[data-tutorial-target="student-name-input"]'
                  );
                  if (!nameInput) {
                    throw new Error('Student name input not found');
                  }

                  const inputDescriptor = Object.getOwnPropertyDescriptor(
                    window.HTMLInputElement.prototype,
                    'value'
                  );
                  if (!inputDescriptor?.set) {
                    throw new Error('Cannot access HTMLInputElement value setter');
                  }
                  inputDescriptor.set.call(nameInput, TUTORIAL_STUDENT_NAME);
                  nameInput.dispatchEvent(new Event('input', { bubbles: true }));
                  nameInput.dispatchEvent(new Event('change', { bubbles: true }));

                  // Click submit button using data-tutorial-target
                  await new Promise((resolve) =>
                    setTimeout(resolve, TUTORIAL_TIMEOUTS.STATE_PROPAGATION)
                  );
                  const submitButton = document.querySelector(
                    '[data-tutorial-target="student-form-submit-button"]'
                  );
                  if (!submitButton) {
                    throw new Error('Student form submit button not found');
                  }
                  (submitButton as HTMLButtonElement).click();
                }
              } catch (error) {
                console.error('Failed to select/create student:', error);
                throw new Error(notifications.tutorial.genericError);
              }
            },
          } as ActionDefinition,
        ],
        waitFor: () => {
          // First ensure the student is selected in the input
          const input = document.querySelector<HTMLInputElement>(
            '[data-tutorial-target="student-select"]'
          );
          
          if (input?.value !== TUTORIAL_STUDENT_NAME) {
            return false;
          }

          // Then ensure the StudentFormModal is closed (if it was opened)
          const studentModal = document.querySelector('[data-tutorial-target="student-form-submit-button"]');
          return studentModal === null;
        },
      },

      {
        id: 'step-9-paper-status',
        title: 'Choose Draft or Final',
        description:
          `🗒 **Draft** - Asks students questions to help them think through fixes

🏁 **Final** - Gives specific suggestions for improvement

We’ll use Draft for this example.`,
        targetSelector: '[data-tutorial-target="status-radios"]',
        placement: 'right',
        allowInteraction: false,
      },

      {
        id: 'step-10-add-paper-text',
        title: 'Paste the Paper',
        description:
`Paste your student’s paper here.

We’ll fill in a sample paper for you.`,
        targetSelector: '[data-tutorial-target="paper-editor"]',
        placement: 'top',
        allowInteraction: false,
        nextButtonLabel: 'Fill Paper',
        timeout: TUTORIAL_TIMEOUTS.PAPER_SAVE,
        onNextActions: [
          {
            id: 'wait-before-fill-essay',
            type: 'wait',
            value: TUTORIAL_TIMEOUTS.STEP_DELAY,
          } as ActionDefinition,
          {
            id: 'fill-essay',
            type: 'custom',
            onExecute: () => {
              try {
                const editorWrapper = document.querySelector(
                  '[data-tutorial-target="paper-editor"]'
                );
                if (!editorWrapper) {
                  throw new Error('Paper editor not found');
                }

                const editor = editorWrapper.querySelector('.tiptap') as HTMLElement;
                if (!editor) {
                  throw new Error('TipTap editor element not found');
                }

                // Focus the editor first
                editor.focus();

                // Try execCommand first (best for TipTap - maintains editor state)
                const essayText = helpers.tutorialContent.essayText;
                const execCommandSuccess = document.execCommand(
                  'insertText',
                  false,
                  essayText
                );

                if (!execCommandSuccess) {
                  // Fallback: direct content manipulation
                  // Note: This may not maintain perfect TipTap state, but it works
                  editor.textContent = essayText;
                  editor.dispatchEvent(new Event('input', { bubbles: true }));
                  editor.dispatchEvent(new Event('change', { bubbles: true }));
                }

                // Verify content was set
                if ((editor.textContent?.length ?? 0) < 100) {
                  throw new Error('Failed to set editor content');
                }
              } catch (error) {
                console.error('Failed to fill paper text:', error);
                throw new Error(notifications.tutorial.genericError);
              }
            },
          } as ActionDefinition,
        ],
        waitFor: () => {
          const editor = document.querySelector(
            '[data-tutorial-target="paper-editor"] .tiptap'
          );
          return ((editor as HTMLElement)?.textContent?.length ?? 0) > 100;
        },
      },

      {
        id: 'step-11-auto-queue-option',
        title: 'Auto-Highlight Is Enabled ✨',
        description:
`With this checked, Markeroo will:
- Highlight the paper automatically
- Create a private share link

You can share the link whenever you’re ready.`,
        targetSelector: '[data-tutorial-target="auto-queue-checkbox"]',
        placement: 'top',
        allowInteraction: false,
      },

      {
        id: 'step-12-save-paper',
        title: 'Save the Paper',
        description: `Everything looks good! When you save, Markeroo will start highlighting the paper.`,
        targetSelector: '[data-tutorial-target="save-paper-button"]',
        placement: 'top',
        allowInteraction: false,
        nextButtonLabel: 'Save Paper',
        timeout: TUTORIAL_TIMEOUTS.PAPER_SAVE,
        onNextActions: [
          {
            id: 'wait-before-save',
            type: 'wait',
            value: TUTORIAL_TIMEOUTS.STEP_DELAY,
          } as ActionDefinition,
          {
            id: 'click-save-paper',
            type: 'click',
            targetSelector: '[data-tutorial-target="save-paper-button"]',
            showRipple: true,
          } as ActionDefinition,
        ],
        waitFor: async () => {
          await new Promise((resolve) => setTimeout(resolve, TUTORIAL_TIMEOUTS.MODAL_TRANSITION));

          const saveButton = document.querySelector('[data-tutorial-target="save-paper-button"]');
          const modalClosed = saveButton === null;

          const paperCards = document.querySelectorAll('[data-component="document-card"]');
          const paperAdded = paperCards.length > 0;

          return modalClosed && paperAdded;
        },
      },

      // ============================================================================
      // PHASE 4: VIEWING MARKUP
      // ============================================================================

      {
        id: 'step-13-paper-processing',
        title: 'Markeroo Highlights the Paper',
        description:
          `Markeroo will read the paper, identifying issues and writing feedback.

This usually takes 30-60 seconds. We’ll fast-forward through it for this tutorial.

Let’s look at the highlighted paper!`,
        targetSelector: '[data-component="document-card"]',
        placement: 'top',
        allowInteraction: false,
        nextButtonLabel: 'View Highlights',
        timeout: 60000,
        onNextActions: [
          {
            id: 'wait-before-view-markup',
            type: 'wait',
            value: TUTORIAL_TIMEOUTS.STEP_DELAY,
          } as ActionDefinition,
          {
            id: 'click-view-markup',
            type: 'click',
            targetSelector: '[data-tutorial-target="view-markup-button"]',
            showRipple: true,
          } as ActionDefinition,
        ],
        waitFor: () => {
          // First wait for the View Markup button to appear
          const viewButton = document.querySelector('[data-tutorial-target="view-markup-button"]');
          if (!viewButton) return false;

          const htmlElement = viewButton as HTMLElement;
          const isHidden = htmlElement.style.display === 'none' ||
                          htmlElement.hasAttribute('hidden') ||
                          htmlElement.classList.contains('hidden');

          if (isHidden) return false;

          // Then wait for the modal to open after clicking
          const modal = document.querySelector('[data-tutorial-target="document-markup-modal"]');
          return modal !== null;
        },
      },

      {
        id: 'step-14-summary-cards',
        title: 'Three Sections of Feedback',
        description: `Markeroo organizes feedback into three sections:

🎉 **Celebrate** - What went well
🎯 **Priorities** - What needs work
💡 **Strategies** - How to improve

This gives you and your student a quick overview.`,
        targetSelector: '[data-tutorial-target="summary-cards"]',
        placement: 'bottom',
        allowInteraction: false,
        // Wait for the document to load and ShareDocumentView to render
        waitFor: () => {
          const summaryCards = document.querySelector('[data-tutorial-target="summary-cards"]');
          return summaryCards !== null;
        },
      },

      {
        id: 'step-15-interact-with-document',
        title: 'Explore the Marked-Up Paper',
        description: `Here’s what you can do:

🔍 **Hover** over highlights to read feedback
✏️ **Select text** to add your own comments
🗑 **Click the trash icon** to remove highlights

Try hovering over highlights to see the feedback!`,
        targetSelector: '[data-tutorial-target="markup-document"]',
        placement: 'top',
        allowInteraction: true,
      },

      {
        id: 'step-16-show-all-markup',
        title: 'Control What Students See',
        description: `By default, students see every instance of each issue.

Check this box to reduce highlights and show just a few examples (to avoid overwhelm).`,
        targetSelector: '[data-tutorial-target="reduce-highlights-checkbox"]',
        placement: 'bottom',
        allowInteraction: false,
      },

      {
        id: 'step-17-share-with-student',
        title: "You’re Ready to Share! 🎉",
        description: `Click 'Share with Alex' to create a private share link for your student. They’ll have read-only access to the feedback.

**Congratulations!** You’ve completed the tutorial.

Click 'Finish' to start using Markeroo.`,
        targetSelector: '[data-tutorial-target="copy-share-link-button"]',
        placement: 'bottom',
        allowInteraction: true,
      },
    ],
  };
}
